<template>
  <div class="container">
    <Teleport to="body">
      <ChartTooltip
        v-if="hoveredCell"
        :label="hoveredCell.dynamicLabel"
        :color="hoveredCell.color"
        :value="
          hoveredCell.value !== undefined && hoveredCell.value !== null
            ? formatter(hoveredCell.value, locale, dataFormat)
            : '-'
        "
        :x="hoveredCell.x"
        :y="hoveredCell.y"
      />
    </Teleport>
    <TableBase
      v-if="table && table.body.length > 0"
      :table="table"
      :sortDirection="'asc'"
      bodyHeight="400px"
      leftWidth="85px"
      layout="fixed"
      rightTextAlign="center"
      caption="Heatmap table"
      @mouseEnterCell="mouseEnterCell"
      @mouseLeaveCell="mouseLeaveCell"
    >
      <template #header-cell="{ cell, position }">
        <template v-if="position !== 'left'">
          <TextBase
            v-if="cell.label"
            :text="cell.label.title"
            :type="position === 'right' || position === 'bottom' ? 'label' : 'tiny'"
          />
        </template>
        <template v-else>
          <div
            :style="{ cursor: 'pointer' }"
            @mouseenter="() => addHoveredItem(cell['id'], { color: '#fadb14' })"
            @mouseleave="removeLastHoveredItem"
          >
            <!-- TooltipStandardV2 removed as its text calculation was broken -->
            <!-- Displaying the formatted title directly -->
            <div v-if="cell.label">
              <TextBase :text="cell.label.title" type="tiny" overflow="ellipsis" />
            </div>
          </div>
        </template>
      </template>
      <template #footer>
        <ColorLegendContinuous :formatOptions="dataFormat" :colorScale="colorScale" />
      </template>
    </TableBase>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import ChartTooltip from '../atoms/ChartTooltip.vue'
import ColorLegendContinuous from '@/design-system/atoms/ColorLegendContinuous.vue'
import { ColorScaleInterpolate, getColorForValue } from '@/utils/colorScale'
import TableBase from '../atoms/TableBase.vue'
import { FormatOptions, formatter } from '@/components/AnalyticsDashboard/formatter'
import { useI18n } from 'vue-i18n'
import TextBase from '../atoms/TextBase.vue'
import { useHoverStore } from '@/pinia/hover'
// Original Props definition removed due to duplication

type HeaderLabel = {
  title: string
  subtitle?: string
}

// Type for the data structure passed via the matrix prop
// (Matches ProcessedHeatmapData in CardHeatmapNew.vue)
type HeatmapTableData = {
  axes: { x: (string | null)[]; y: (string | null)[] }
  matrix: (number | null)[][]
  summary: { x: (number | null)[]; y: (number | null)[] }
  tooltipLabelsMatrix: string[][] // Expect pre-computed labels
}

type HoveredCellData = {
  value: number | null
  color: string
  x: number // Mouse X
  y: number // Mouse Y
  dynamicLabel: string // Label comes from tooltipLabelsMatrix
}

const { locale } = useI18n()
const { addHoveredItem, removeLastHoveredItem } = useHoverStore()

// Adjust Prop type to expect the new structure in 'matrix'
type Props = {
  matrix?: HeatmapTableData // Expect the full processed data object here
  xSumLabel?: string
  ySumLabel?: string
  // tooltipLabel prop is removed
  fullscreen?: boolean
  colorScale: ColorScaleInterpolate
  dataFormat?: FormatOptions // For cell values and summaries
  xAxisFormat?: FormatOptions // For X-axis labels
  yAxisFormat?: FormatOptions // For Y-axis labels
  start: number
  end: number
}

const props = defineProps<Props>()

const table = computed(() =>
  props.matrix
    ? {
        body: props.matrix.matrix.map(
          (
            row,
            rowIndex // Get rowIndex here
          ) =>
            paginate(row).map((value, colIndex) => {
              // Get paginated colIndex here
              const color =
                value !== undefined && value !== null
                  ? getColorForValue(props.colorScale, value).toHex()
                  : '#fff'
              return {
                value,
                color,
                style: { backgroundColor: color },
                class: 'custom-cell',
                rowIndex: rowIndex, // Add rowIndex
                colIndex: colIndex // Add paginated colIndex
              }
            })
        ),
        headers: {
          top: paginate(props.matrix.axes.x).map((label) => ({
            label: {
              title: label ? formatter(label, locale.value, props.xAxisFormat) : '-'
            } as HeaderLabel,
            class: 'label-x'
          })),
          left: props.matrix.axes.y.map((id) => ({
            label: {
              title: id ? formatter(id, locale.value, props.yAxisFormat) : '-'
            } as HeaderLabel,
            id,
            class: 'label-y'
          })),
          bottomLeft: { label: { title: props.xSumLabel } as HeaderLabel, class: 'label-x' },
          bottom:
            props.ySumLabel !== undefined
              ? paginate(props.matrix.summary.x).map((sum) => ({
                  label: {
                    title: sum !== null ? formatter(sum, locale.value, props.dataFormat) : '-'
                  } as HeaderLabel,
                  class: 'custom-sum'
                }))
              : undefined,
          topRight:
            props.xSumLabel !== undefined
              ? { label: { title: props.ySumLabel } as HeaderLabel, class: 'label-x' }
              : undefined,
          right:
            props.xSumLabel !== undefined
              ? props.matrix.summary.y.map((sum) => ({
                  label: {
                    title: sum !== null ? formatter(sum, locale.value, props.dataFormat) : '-'
                  } as HeaderLabel,
                  class: 'custom-sum'
                }))
              : undefined
        }
      }
    : undefined
)

const hoveredCell = ref<HoveredCellData | undefined>()

// Define the type for the cell data passed by TableBase, now including our added indices
type EmittedCellData = {
  value: number | null
  color: string | undefined
  rowIndex: number
  colIndex: number // This is the paginated index
  // other potential properties from TableBase's cell object if needed
}

const mouseEnterCell = (
  // TableBase emits (cell, centerX, centerY)
  // Our 'cell' now contains value, color, rowIndex, colIndex, style, class
  cell: EmittedCellData,
  centerX: number,
  centerY: number
) => {
  const { value, color, rowIndex, colIndex } = cell

  if (value === null || color === undefined || !props.matrix) {
    hoveredCell.value = undefined // Clear tooltip if no value/color
    return
  }

  // Get the pre-computed label from the matrix data using the correct indices
  // We use the paginated colIndex directly as the tooltipLabelsMatrix should correspond to the filtered+paginated view
  const dynamicLabel = props.matrix?.tooltipLabelsMatrix?.[rowIndex]?.[colIndex] ?? '?'

  hoveredCell.value = {
    value,
    color,
    x: centerX,
    y: centerY,
    dynamicLabel // Use the pre-computed label
  }
}

const mouseLeaveCell = () => {
  hoveredCell.value = undefined
}

function paginate<T>(array: T[]): T[] {
  return array.slice(Math.max(0, props.start), Math.min(array.length, props.end))
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';
.color-legends-box {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding-left: 104px;
}

.container {
  position: relative;
}

.header-label {
  width: 100%;
  overflow-x: hidden;
  text-wrap: nowrap;
  text-overflow: ellipsis;
}

:deep(*) {
  .cell {
    position: relative;
    height: 100%;
    display: flex;
    justify-content: center;
    vertical-align: middle;
  }
  .cell-width {
    width: 104px;
  }
  .custom-table {
    border-collapse: collapse;
  }

  .label-y {
    @include font-mixins.desktop-regular-tiny;
    padding: 8px;
    height: 40px;
    text-align: start;
    text-wrap: nowrap;
    color: variables.$color-greyscale-content;
    text-overflow: ellipsis;
  }

  .label-x {
    @include font-mixins.desktop-bold-tiny;
    padding: 8px;
    padding-top: 16px;
    color: variables.$color-greyscale-secondary;
  }
  .custom-cell {
    @include font-mixins.desktop-regular-label;
    height: 40px;
    text-align: center;
    position: relative;
    border: 1px solid #fff;
    .fullscreen & {
      height: 40px;
    }
  }

  .custom-sum {
    @include font-mixins.desktop-regular-label;
    height: 40px;
    border: 1px solid #fff;
    text-align: center;
    background-color: #f9f9f9;
    color: variables.$color-greyscale-content;
    .fullscreen & {
      height: 40px;
    }
  }
}
</style>
